import { useState } from "react";
import DropBox from "../../../../component/DropBox";
import PopupPagination from "../../../../component/Pagination/popupPagination";
import {
  deletePubMissionBingo,
  endPubMissionBingo,
  SellerBingoSizeFilter,
  SellerPubMissionBingoListItemResponse,
  SellerPubMissionBingoStatus,
} from "../../../../api/pub-mission-bingo";
import DeleteBlockedPopup from "../../../event/shared/delete-blocked-popup";
import DeleteConfirmPopup from "../../../event/shared/delete-confirm-popup";
import EndConfirmPopup from "../../../event/shared/end-confirm-popup";
import * as S from "./styles";

interface TableProps {
  list: SellerPubMissionBingoListItemResponse[];
  pubId: number;
  sizeFilter: SellerBingoSizeFilter;
  searchQuery: string;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  handleSearchQueryChange: (query: string) => void;
  handleSizeFilterChange: (size: SellerBingoSizeFilter) => void;
  onRefresh: () => Promise<void>;
  onEdit: (bingo: SellerPubMissionBingoListItemResponse) => void;
}

const SIZE_FILTER_LIST = [
  { name: "전체 규격", value: SellerBingoSizeFilter.ALL },
  { name: "3×3", value: SellerBingoSizeFilter.THREE },
  { name: "4×4", value: SellerBingoSizeFilter.FOUR },
];

const COLUMNS = [
  { header: "빙고명", className: "name" },
  { header: "규격", className: "size" },
  { header: "진행 기간", className: "period" },
  { header: "보상 단계", className: "reward" },
  { header: "등록일", className: "createdAt" },
  { header: "상태", className: "status" },
  { header: "관리", className: "action" },
];

const getStatusClassName = (status: SellerPubMissionBingoStatus) => {
  if (status === SellerPubMissionBingoStatus.WAITING) return "waiting";
  if (status === SellerPubMissionBingoStatus.IN_PROGRESS) return "in-progress";
  return "ended";
};

const Table = ({
  list,
  pubId,
  sizeFilter,
  searchQuery,
  totalPages,
  currentPage,
  setCurrentPage,
  handleSearchQueryChange,
  handleSizeFilterChange,
  onRefresh,
  onEdit,
}: TableProps) => {
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [deletingTarget, setDeletingTarget] =
    useState<SellerPubMissionBingoListItemResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [endingTarget, setEndingTarget] =
    useState<SellerPubMissionBingoListItemResponse | null>(null);
  const [ending, setEnding] = useState(false);

  const onSearch = () => {
    handleSearchQueryChange(searchInput.trim());
  };

  const handleEndBingo = (
    bingo: SellerPubMissionBingoListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (bingo.status === SellerPubMissionBingoStatus.ENDED) return;
    setEndingTarget(bingo);
  };

  const handleConfirmEnd = async () => {
    if (!endingTarget) return;

    setEnding(true);
    try {
      await endPubMissionBingo(endingTarget.id, pubId);
      setEndingTarget(null);
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || "빙고 종료에 실패했습니다.");
    } finally {
      setEnding(false);
    }
  };

  const handleDeleteBingo = (
    bingo: SellerPubMissionBingoListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    if (!bingo.canDelete) {
      setBlockedOpen(true);
      return;
    }
    setDeletingTarget(bingo);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTarget) return;

    setDeleting(true);
    try {
      await deletePubMissionBingo(deletingTarget.id, pubId);
      setDeletingTarget(null);
      await onRefresh();
    } catch (error: any) {
      alert(error?.message || "빙고 삭제에 실패했습니다.");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditBingo = (
    bingo: SellerPubMissionBingoListItemResponse,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    onEdit(bingo);
  };

  return (
    <S.TableWrapper>
      <S.FilterRow>
        <DropBox
          list={SIZE_FILTER_LIST}
          value={sizeFilter}
          setValue={handleSizeFilterChange}
          width="140px"
        />
        <S.SearchBox>
          <input
            type="text"
            placeholder="빙고명 검색"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyUp={(e) => {
              if (e.key === "Enter") onSearch();
            }}
          />
          <div className="searchIcon" onClick={onSearch}>
            <img
              src="/image-web/timer-config/searchIcon.svg"
              alt="검색 아이콘"
            />
          </div>
        </S.SearchBox>
      </S.FilterRow>

      <S.TableContent>
        <S.TableHeader>
          {COLUMNS.map((column) => (
            <div key={column.className} className={`item ${column.className}`}>
              {column.header}
            </div>
          ))}
        </S.TableHeader>

        <S.ListWrapper>
          {list.length === 0 ? (
            <S.EmptyMessage>등록된 빙고가 없습니다.</S.EmptyMessage>
          ) : (
            list.map((item) => (
              <S.TableItem key={item.id}>
                <div className="item name">{item.name}</div>
                <div className="item size">{item.sizeLabel}</div>
                <div className="item period">{item.periodLabel}</div>
                <div className="item reward">{item.rewardStageLabel}</div>
                <div className="item createdAt">{item.createdAt}</div>
                <div className="item status">
                  <span className={`badge ${getStatusClassName(item.status)}`}>
                    {item.statusLabel}
                  </span>
                </div>
                <div className="item action">
                  <button
                    type="button"
                    className="edit"
                    onClick={(event) => handleEditBingo(item, event)}
                  >
                    수정
                  </button>
                  {item.status !== SellerPubMissionBingoStatus.ENDED && (
                    <button
                      type="button"
                      className="end"
                      onClick={(event) => handleEndBingo(item, event)}
                    >
                      종료
                    </button>
                  )}
                  <button
                    type="button"
                    className="delete"
                    onClick={(event) => handleDeleteBingo(item, event)}
                  >
                    삭제
                  </button>
                </div>
              </S.TableItem>
            ))
          )}
        </S.ListWrapper>
      </S.TableContent>

      {totalPages > 1 && (
        <S.PaginationBox>
          <PopupPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </S.PaginationBox>
      )}

      {blockedOpen && (
        <DeleteBlockedPopup
          targetLabel="빙고"
          onClose={() => setBlockedOpen(false)}
        />
      )}

      {deletingTarget && (
        <DeleteConfirmPopup
          targetLabel="빙고"
          targetName={deletingTarget.name}
          submitting={deleting}
          onClose={() => {
            if (!deleting) setDeletingTarget(null);
          }}
          onConfirm={handleConfirmDelete}
        />
      )}

      {endingTarget && (
        <EndConfirmPopup
          targetLabel="빙고"
          targetName={endingTarget.name}
          submitting={ending}
          onClose={() => {
            if (!ending) setEndingTarget(null);
          }}
          onConfirm={handleConfirmEnd}
        />
      )}
    </S.TableWrapper>
  );
};

export default Table;
